// Payment-success and questionnaire-done confirmations.
import { useMemo } from "react";
import { useLang } from "../../i18n.jsx";
import { planLabel } from "../constants.js";
import { FluentEmoji, Stars } from "../emoji.jsx";
import { fmt, toFa } from "../format.js";
import { getPlatform } from "../platform.js";
import { planStories } from "../plans.js";
import { C, FONT } from "../../design-system.js";

// ── Payment Success (with confetti) ───────────────────────────────────────────
export function PaymentSuccessScreen({ plan, childName, ready = true, onDone }) {
  const { t, dir } = useLang();
  const confetti = useMemo(() =>
    Array.from({ length: 30 }).map((_, i) => ({
      left: Math.random() * 100, delay: Math.random() * 0.5, dur: Math.random() * 1.5 + 1.5,
      color: [C.gold, C.teal, C.purple, C.goldLight, C.cream][i % 5], size: Math.random() * 6 + 5,
      rot: Math.random() * 360,
    })), []);

  return (
    <div style={{ minHeight: "100vh", background: C.bgDeep, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 28, fontFamily: FONT, position: "relative", overflow: "hidden" }}>
      <Stars count={40} />
      {/* Confetti */}
      <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
        {confetti.map((c, i) => (
          <div key={i} style={{ position: "absolute", top: -20, left: `${c.left}%`, width: c.size, height: c.size, background: c.color, borderRadius: 2, transform: `rotate(${c.rot}deg)`, animation: `confettiFall ${c.dur}s ease-in ${c.delay}s forwards` }} />
        ))}
      </div>

      <div style={{ position: "relative", textAlign: "center", maxWidth: 320 }}>
        <FluentEmoji anim="bounce" size={84} style={{ marginBottom: 16 }}>✨</FluentEmoji>
        <h2 style={{ color: C.cream, fontSize: 24, fontWeight: 800, margin: "0 0 10px" }}>{t("پرداخت موفق!")}</h2>
        <p style={{ color: C.textMid, fontSize: 16, lineHeight: 1.8, margin: "0 0 28px" }}>
          {childName
            ? t("اشتراک {plan} برای {name} فعال شد.", { plan: planLabel(plan.months), name: childName })
            : t("اشتراک {plan} فعال شد.", { plan: planLabel(plan.months) })}<br />
          {/* A plan can be bought BEFORE the child / questionnaire exist (2026-10-27);
              then nothing has started yet, and saying it had would be a lie. */}
          {ready
            ? t("پرسشنامه از قبل تکمیل شده — ساخت اولین داستان همین حالا آغاز می‌شود 🌙")
            : t("حالا فرزندتان را اضافه کنید و پرسشنامه را پر کنید؛ قصه‌ها از همان لحظه شروع می‌شوند و روزهای اشتراک هم از همان روز شمرده می‌شود 🌙")}
        </p>
        <div style={{ background: C.bgCard, border: `1px solid ${C.tealDim}`, borderRadius: 18, padding: 20, marginBottom: 28, textAlign: dir === "ltr" ? "left" : "right" }}>
          {[[t("نوع اشتراک"), planLabel(plan.months), C.teal], [t("مبلغ پرداختی"), getPlatform() === "telegram" ? `${toFa(plan.stars)} ⭐` : t("{n} تومان", { n: fmt(plan.price) }), C.cream], [t("داستان‌های دریافتی"), t("{n} داستان صوتی", { n: toFa(planStories(plan)) }), C.cream]].map(([k, v, col], i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: i < 2 ? `1px solid ${C.border}` : "none" }}>
              <span style={{ color: C.textMid, fontSize: 15 }}>{k}</span>
              <span style={{ color: col, fontSize: 15, fontWeight: 700 }}>{v}</span>
            </div>
          ))}
        </div>
        <button onClick={onDone} className="press"
          style={{ width: "100%", background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, color: C.bgDeep, border: "none", borderRadius: 16, padding: "16px 0", fontSize: 18, fontWeight: 800, cursor: "pointer", fontFamily: FONT, boxShadow: `0 4px 20px ${C.gold}44` }}>
          {t("بازگشت به خانه 🌙")}
        </button>
      </div>
    </div>
  );
}

// ── Questionnaire Done ────────────────────────────────────────────────────────
// `needsPlan` is the funnel's whole point: the parent has just invested ~70 answers
// and this is the warmest moment to ask for the plan, so the plans screen is the
// PRIMARY action here — not a detour they have to find again from home.
export function QuestionnaireDoneScreen({ needsPlan, childName, onChoosePlan, onDone }) {
  const { t } = useLang();
  return (
    <div style={{ minHeight: "100vh", background: C.bgDeep, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 28, fontFamily: FONT, position: "relative", overflow: "hidden" }}>
      <Stars count={40} />
      <div style={{ position: "relative", textAlign: "center", maxWidth: 320 }}>
        <FluentEmoji anim="bounce" size={76} style={{ marginBottom: 16 }}>{needsPlan ? "🌟" : "📋"}</FluentEmoji>
        <h2 style={{ color: C.cream, fontSize: 24, fontWeight: 800, margin: "0 0 10px" }}>
          {needsPlan && childName ? t("{name} را شناختیم!", { name: childName }) : t("پرسشنامه تکمیل شد!")}
        </h2>
        <p style={{ color: C.textMid, fontSize: 16, lineHeight: 1.8, margin: "0 0 28px" }}>
          {needsPlan
            ? t("حالا می‌دانیم قصه‌های {name} باید چه شکلی باشند. برای شروع، یکی از پلن‌ها را انتخاب کنید.", { name: childName })
            : t("پاسخ‌های شما با دقت بررسی می‌شوند تا داستان‌هایی متناسب با روحیه فرزندتان طراحی شود. 🌙")}
        </p>
        <button onClick={needsPlan ? onChoosePlan : onDone} className="press celebrate glow"
          style={{ width: "100%", background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, color: C.bgDeep, border: "none", borderRadius: 16, padding: "16px 0", fontSize: 18, fontWeight: 800, cursor: "pointer", fontFamily: FONT, boxShadow: `0 4px 20px ${C.gold}44` }}>
          {needsPlan ? t("انتخاب پلن و شروع قصه‌ها ✨") : t("بازگشت به خانه")}
        </button>
        {needsPlan && (
          <button onClick={onDone}
            style={{ width: "100%", background: "none", border: "none", color: C.textMid, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: FONT, padding: "14px 0 0" }}>
            {t("بعداً انتخاب می‌کنم")}
          </button>
        )}
      </div>
    </div>
  );
}
